import {
  AddressType,
  AreaPageListing,
  AreaPageListingResponse,
  SingleAddress,
} from '@zoocasa/go-search';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { deDasherize } from '@zoocasa/node-kit/strings/de-dasherize';
import {
  AREA_LISTINGS_ROUTE,
} from 'components/dynamic-page/route-matchers';
import { CANADA_ADDRESS, US_ADDRESS } from 'constants/address';
import { CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE } from 'constants/headers';
import { AREA_PAGE_CONSTANTS } from 'constants/pagination';
import { FeaturesType } from 'contexts';
import {
  AddressHierarchyPath,
  createAddressHierarchyPath,
  parseAddressPath,
  ParsedAddressPath,
  ParsedLocation,
  UNSUPPORTED_ADDRESS_TYPES,
} from 'data/addresses';
import { Breadcrumb, createBreadcrumbs } from 'components/breadcrumbs';
import { DEFAULT_LISTING_PARAMS_FILTER_SHARED } from 'contexts/preferences/listing-params/defaults';
import { ASCENDING_ORDER, sortBy } from 'data/listing';
import { SearchByAreaFilterType } from 'data/search/area/types';
import deepmerge from 'deepmerge';
import { cloneDeep } from 'lodash';
import { CountryCode, CountryCodeList } from 'types/countries';
import { Nullable, NullableType } from 'types/nullable';
import isUserAgentCrawler, { isUserAgentLighthouse } from 'utils/crawler-agent';
import { removeUndefined } from 'utils/objects';
import { ProvinceAndState, ProvinceOrStateCode } from 'utils/province_or_state';
import { getSiteLocationFromRequest } from 'utils/site-location';
import { getSortBy } from 'utils/sort';
import { generateLocalizedCityFooterData, generateLocalizedNeighbourhoodFooterData } from '../area-listings/generate-dynamic-footer-data';
import generateHeading from '../area-listings/util/generate-heading';
import { shouldShowUntranslatedContent } from 'utils/locale/should-show-untranslated-content';
import { LocaleManager } from 'utils/locale/locale-manager';
import { InsightsApiType } from 'data/insights';
import type { InsightsListingType } from 'data/insights/types';
import { getInsightsListingType } from 'components/pages/area-page-v2/insights-listing-type';
import { getCityInsights } from 'data/city-insights/fetcher';
import {
  selectLocalizedCityInsights,
  normalizeLocaleToInsightsLang,
  isLocaleTranslationMissing,
} from 'utils/city-insights/localize';
import type { StoredCityInsights } from 'utils/city-insights/localize';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { restoreRewrittenQueryParams } from 'utils/urls/restore-rewritten-query';
import { stripAffiliateHandle } from 'utils/urls/strip-affiliate-handle';
import { generateHreflangFromCanonicalUrl } from 'utils/urls/hreflangUrls';
import { HeadDataType } from 'components/head-data';
import { HeadTagsBuilder, PropertyInsights, buildHeadTags } from '../area-listings/util/head-tags-builder';
import { applySeoRules } from 'utils/seo';
import { NOT_AVAILABLE_SOLD_STATUS, Sort } from 'contexts/preferences/listing-params/types';
import { getTenantSeoConfig } from '@tenants/utils/configs';
import { getMapKitToken } from 'utils/apple-maps/token';


import { GeoSearchFilter } from 'data/search/map/types';
import { getListingsV2MapAreaSlug } from 'data/search/map/listings-v2-map-area-slug';
import { getGeohashPrecisionForHybridSsrBoundary, getOptimalZoomLevelForHybridPage } from 'components/search-es/geo_helper';

import type { SearchApiType } from 'data/search';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { DynamicPageServerSideProps } from 'types/dynamic_page_types';
import type { AddressViewModel, AreaInsightsData, AreaPageV2AreaInsights, HybridPageViewModel, MilitaryBaseViewModel } from './hybrid_page_view_model';
import type { NamedContent, NamedContentsApiType } from 'data/named-content';
import type { AddressesApiType } from 'data/addresses';
import type { SeoLinksApiType } from 'data/seo-links';
import type { TenantTypes } from '@tenants/manager';
import type { ServerTranslationContext } from 'utils/translations/server-translation';

//#region Types
export type HybridPageServerSideProps = Required<Nullable<HybridPageViewModel<AddressViewModel | MilitaryBaseViewModel>>>;

  type GetServerSidePropsReturnType = Promise<GetServerSidePropsResult<DynamicPageServerSideProps<HybridPageServerSideProps>>>;

export type AreaPageListings = {
  /** The address used to fetch listings. */
  electedAddress?: SingleAddress;
  /** The listings fetched from the search API. */
  areaPageListingResponse?: AreaPageListingResponse;
  nextPageCursor?: string;
};

//#endregion

//#region Constants
const DEFAULT_NOT_FOUND_RESPONSE: GetServerSidePropsResult<DynamicPageServerSideProps<HybridPageServerSideProps>> = { notFound: true };
//#endregion


async function getListings(addresses: SingleAddress[], filter: SearchByAreaFilterType, sort: Sort, pageNumber: number, pageSize: number, searchApi: SearchApiType, parsedRequestAddress?: ParsedAddressPath): Promise<AreaPageListings | undefined> {
  if (addresses && addresses?.length > 0) {
    const apiFilter = cloneDeep(deepmerge(DEFAULT_LISTING_PARAMS_FILTER_SHARED, filter));
    
    // We start from the most specific address and work our way up to the least specific address to find listings  
    for (let index = addresses.length - 1; index >= 0; index--) {
      const address = addresses[index];
      const areaSlug = getListingsV2MapAreaSlug(address, parsedRequestAddress);

      if (!areaSlug) {
        console.warn(`getListings: no areaSlug computed for address slug="${address.slug}" type=${address.addressType} — falling back to a broader address`);
        continue;
      }

      const optimalZoom = getOptimalZoomLevelForHybridPage(address);
      const precision = getGeohashPrecisionForHybridSsrBoundary(optimalZoom);
      

      const geoFilter: GeoSearchFilter = {
        ...apiFilter,
        countryCode: address.countryCode as CountryCode,
      };
      

      const pageCursor = '';
      
      try {
        const boundaryResponse = await searchApi.searchListingsInBoundary(
          null, // No bounds for initial load
          precision,
          pageCursor,
          geoFilter,
          getSortBy(sort),
          undefined,
          pageSize,
          areaSlug
        );
        
        // Convert MapPageBoundarySearchResponse to AreaPageListingResponse format
        const areaPageListingResponse: AreaPageListingResponse = {
          data: boundaryResponse.listings,
          TotalPages: boundaryResponse.totalPages,
          TotalCount: boundaryResponse.totalCount,
          PageNumber: pageNumber,
          PageSize: pageSize,
        };
        
        // Only return if we found listings, otherwise try next (less specific) address level
        if (areaPageListingResponse.TotalCount > 0) {
          return {
            electedAddress: address,
            areaPageListingResponse,
            nextPageCursor: boundaryResponse.nextPage || null,
          };
        }
        console.warn(`getListings: areaSlug="${areaSlug}" (address slug="${address.slug}" type=${address.addressType}) returned 0 results — falling back to a broader address`);
      } catch (error) {
        console.error(`Error fetching listings for address ${index}:`, error);
        continue;
      }
    }
  }
  return undefined;
}

export default async function getServerSideProps(context: GetServerSidePropsContext, features: FeaturesType, tenant: TenantTypes, searchApi: SearchApiType, addressesApi: AddressesApiType, namedContentsApi: NamedContentsApiType, insightsApi: InsightsApiType, seoLinksApi: SeoLinksApiType, i18n?: ServerTranslationContext): GetServerSidePropsReturnType {
  context.res.setHeader(CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE);

  // Read the missing address query parameter (set by middleware when redirecting from non-existent address pages)
  const missingAddressParam = context.query.missingAddress;
  const missingAddress = typeof missingAddressParam === 'string' ? missingAddressParam : null;

  const siteCountry = getSiteLocationFromRequest(context.req);
  // The middleware strips the handle before routing, but resolvedUrl still reflects what
  // the browser sent (e.g. /toronto-real-estate/@JohnSmith). Strip it here so address
  // parsing never sees the handle segment and misidentifies it as a neighbourhood slug.
  // Middleware rewrites (e.g. the hide-imageless-listings user setting turning `/houses` into
  // `/filter?...`) surface their query params only in context.query — resolvedUrl drops them.
  // Restore them so address parsing sees the same filters the middleware produced.
  const resolvedUrlWithQuery = stripAffiliateHandle(restoreRewrittenQueryParams(context.resolvedUrl, context.query));
  const parsedAreaPathFromUrl = parseAddressPath(resolvedUrlWithQuery.replace(`/${AREA_LISTINGS_ROUTE}`, '')); // This is the URl path as a parsed hierarchical representation of the requested addresses

  if (parsedAreaPathFromUrl.length === 0) {
    return DEFAULT_NOT_FOUND_RESPONSE; // If the area path is empty, we don't know what the user is looking for and we should return a 404.
  }

  // There's only one case where this path will have more than one ParsedAddressPath element and it only happens when we cannot infer if
  // the url was pointing to a neighbourhood or an address. This should have been handled by our router matcher and at this point we should
  // have only areas (cities, neighbourhoods, provinces/states, countries) and can safely ignore segments that are address types.
  const requestedAddress = (parsedAreaPathFromUrl.length === 1) ? parsedAreaPathFromUrl[0] : parsedAreaPathFromUrl.filter(segment => segment.type === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD)[0];
  const requestedAreaSlug: string = requestedAddress.slug;

  const tenantFilter = tenant.theme.id; // FIXME: Legacy code used the theme name as the tenant filter. This is a temporary fix to ensure that the tenant filter is set correctly.
  const partialFilter: SearchByAreaFilterType = Object.freeze({ ...requestedAddress.filter, tenant: tenantFilter, slug: requestedAreaSlug });
  const fullFilter = Object.freeze(cloneDeep(deepmerge(DEFAULT_LISTING_PARAMS_FILTER_SHARED, partialFilter)));
  const isSoldFilterOn = fullFilter.status === NOT_AVAILABLE_SOLD_STATUS;

  const sort: Sort = requestedAddress.sort;
  const page: number = Math.min(requestedAddress.page, AREA_PAGE_CONSTANTS.MAX_PAGE_COUNT);
  const pageSize: number = AREA_PAGE_CONSTANTS.DEFAULT_PAGE_SIZE;

  const requestedAreaName = requestedAddress.neighbourhood?.name || requestedAddress.subDivision?.name || requestedAddress.provinceOrState?.name || requestedAddress.country?.name || '';

  const requestedAreaPageAddress = await addressesApi.getAddressesBySlug(requestedAreaSlug);
  let fallbackAreaPageAddress: NullableType<SingleAddress[]> = null;
  let electedAreaPageAddress: SingleAddress[];

  const isUnknownAddress = requestedAreaPageAddress?.length === 0;
  if (isUnknownAddress) {
    // The requested address cannot be found, we need to fallback to a different address.
    const addressProperties: (keyof ParsedAddressPath)[] = ['neighbourhood', 'subDivision', 'provinceOrState', 'country'];
    for (const property of addressProperties) {
      if (requestedAddress[property]) {
        const addressSegment = requestedAddress[property] as ParsedLocation;
        const address = await addressesApi.getAddressesBySlug(addressSegment.slug);
        if (address?.length > 0) {
          fallbackAreaPageAddress = address;
          break;
        }
      }
    }
    if (fallbackAreaPageAddress?.length === 0) {
      fallbackAreaPageAddress = [siteCountry === CountryCodeList.UNITED_STATES ? US_ADDRESS : CANADA_ADDRESS];
    }
    electedAreaPageAddress = fallbackAreaPageAddress;
  } else {
    electedAreaPageAddress = requestedAreaPageAddress;
  }

  const electedAreaPageAddressHierarchy = createAddressHierarchyPath(electedAreaPageAddress);
  if (!electedAreaPageAddressHierarchy) {
    return DEFAULT_NOT_FOUND_RESPONSE; // If we cannot create an address hierarchy path, we don't know what the user is looking for and we should return a 404.
  }

  if (UNSUPPORTED_ADDRESS_TYPES.includes(electedAreaPageAddressHierarchy.type)) {
    console.error(`area page received a request to display an address that is not supported: ${requestedAreaSlug}`);
    return DEFAULT_NOT_FOUND_RESPONSE; // If the requested area is a listing address we should not try to open in an area page. This should never happen. This is a bug.
  }

  let getListingsResponse = await getListings(electedAreaPageAddress, partialFilter, sort, page, pageSize, searchApi, requestedAddress);

  if (!getListingsResponse) {
    if (fallbackAreaPageAddress && electedAreaPageAddress[electedAreaPageAddress.length - 1] !== fallbackAreaPageAddress[fallbackAreaPageAddress.length - 1]) {
      // The address exist but we cannot find any listings, we try to find listings for the fallback address if we have one
      getListingsResponse = await getListings(fallbackAreaPageAddress, partialFilter, sort, page, pageSize, searchApi, requestedAddress);
    }

    if (!getListingsResponse) { // The address exists but we cannot find any listings, we return an empty response
      getListingsResponse = { areaPageListingResponse: { TotalPages: 0, TotalCount: 0, PageNumber: 0, PageSize: 0, data: []}, electedAddress: electedAreaPageAddress[electedAreaPageAddress.length - 1], nextPageCursor: null };
    }
  }

  const { areaPageListingResponse, electedAddress } = getListingsResponse;
  const { TotalPages, TotalCount, PageNumber, PageSize, data: listings } = areaPageListingResponse || { TotalPages: 0, TotalCount: 0, PageNumber: 0, PageSize: 0, data: []};
  const listingsSortedByPrice = sortBy(listings, (listing: AreaPageListing) => listing?.price, ASCENDING_ORDER);
  const lowestPrice = listingsSortedByPrice?.[0]?.price;
  const highestPrice = listingsSortedByPrice?.[listingsSortedByPrice.length -1 ]?.price;

  const isFallbackArea = requestedAreaSlug != electedAddress.slug;
  const breadcrumbs: readonly Breadcrumb[] | undefined = createBreadcrumbs(electedAreaPageAddressHierarchy);

  const resolvedPageAddress = electedAreaPageAddress[electedAreaPageAddress.length - 1];
  // A province/state-level address (e.g. the "ontario-real-estate" page) IS the province being
  // shown, so its own provinceOrState must not also be appended as a suffix (that previously
  // produced "Ontario, ON" instead of just "Ontario") — only do this at city/neighbourhood level.
  const isResolvedAddressCityOrNeighbourhoodLevel = Boolean(resolvedPageAddress?.subDivision || resolvedPageAddress?.neighbourhood);
  const areaNameText = resolvedPageAddress
    ? (isResolvedAddressCityOrNeighbourhoodLevel && resolvedPageAddress.provinceOrState ? `${resolvedPageAddress.label}, ${resolvedPageAddress.provinceOrState}` : resolvedPageAddress.label)
    : requestedAreaName;
  // Non-default filters (home type, sold/rental status, bedrooms, etc.) mean the "There are X+
  // listings" answer-style heading no longer describes the page — fall back to the classic
  // filter-aware heading ("Houses For Sale in Toronto", "Toronto Recently Sold Condos", etc).
  const hasNonDefaultFilters = !!requestedAddress.filter && Object.keys(requestedAddress.filter).length > 0;
  const filterAwareHeading = hasNonDefaultFilters
    ? generateHeading({
      areaName: requestedAreaName,
      areaType: requestedAddress.type,
      filter: fullFilter,
      city: requestedAddress.subDivision?.name,
      neighbourhood: requestedAddress.neighbourhood?.name,
    })
    : null;
  const heading = filterAwareHeading
    ?? (areaNameText
      ? `There are ${TotalCount.toLocaleString('en-US')}+ Real Estate Property Listings in ${areaNameText}`
      : `There are ${TotalCount.toLocaleString('en-US')}+ Real Estate Property Listings`);
  // AreaPageV2's header drops the count (it duplicates, and drifts from, the live map-area
  // count in the subheading) — see HybridPageViewModel.headingWithoutCount.
  const headingWithoutCount = filterAwareHeading
    ?? (areaNameText
      ? `Real Estate Property Listings in ${areaNameText}`
      : 'Real Estate Property Listings');
  const expandGuidesByDefault = isUserAgentCrawler(context.req.headers);
  const isCrawlerOrLighthouse = isUserAgentCrawler(context.req.headers) || isUserAgentLighthouse(context.req.headers['user-agent']);

  const internalLinks = await getInternalLinks(electedAreaPageAddressHierarchy, fullFilter.latitude, fullFilter.longitude, tenant, isSoldFilterOn) || [];
  const seoLinks = await getSeoLinks(electedAddress, seoLinksApi, isSoldFilterOn);

  // Only show area guides when locale matches tenant default (typically English variants)
  const shouldShowAreaGuides = shouldShowUntranslatedContent({
    tenantId: tenant.id,
    routeLocale: context.locale,
    requestCookies: context.req.headers.cookie,
  });

  let areaGuideData: AreaInsightsData | null = null;

  if (!isUnknownAddress && shouldShowAreaGuides) {
    const address = electedAreaPageAddress[electedAreaPageAddress.length - 1];
    const country = address.countryCode as CountryCode;
    const provinceOrState = address.provinceOrState?.toLowerCase() as ProvinceOrStateCode;
    const city = address.subDivision;

    const topCities = await insightsApi.getTopCities(country, provinceOrState);
    const cityStats = await insightsApi.getCityStats(country, provinceOrState, city);

    areaGuideData = {
      areaNamedContent: null,
      areaBlurb: null,
      areaBlurbHeading: null,
      areaInsightsData: {
        areaData: cityStats,
        surroundingCities: topCities?.map(city => ({
          city: city.Name,
          province: city.Province,
          slug: city.Slug,
        })),
      },
    };

    const hasHomeTypeFilter = partialFilter.homeType !== undefined;
    let namedContents: NamedContent[] | undefined;

    if (hasHomeTypeFilter) {
      namedContents = await namedContentsApi.getAreaPageNamedContent(requestedAreaSlug, fullFilter.homeType);
      if (namedContents?.length === 0) {
        // If we don't have any named content for the home type, we fallback to the generic named content
        namedContents = await namedContentsApi.getAreaPageNamedContent(requestedAreaSlug);
      }
    } else {
      namedContents = await namedContentsApi.getAreaPageNamedContent(requestedAreaSlug);
    }

    if (namedContents?.length > 0) {
      const namedContent = namedContents[0];
      const hasAreaBlurb = namedContent.content.includes('DESCRIPTION BREAK');
      const splitNamedContentData = hasAreaBlurb ? namedContent.content.split('<p>DESCRIPTION BREAK</p>') : namedContent.content;
      const areaBlurb = hasAreaBlurb ? splitNamedContentData[0] : null;
      const areaNamedContent = hasAreaBlurb ? splitNamedContentData[1]?.split('<p>SECTION BREAK</p>') : (splitNamedContentData as string).split('<p>SECTION BREAK</p>');

      areaGuideData = {
        ...areaGuideData,
        areaNamedContent: (areaNamedContent || []).map((content: string) => content.replace(/\n/g, '')),
        areaBlurb,
        areaBlurbHeading: generateAreaBlurbHeading(requestedAddress?.subDivision?.name, requestedAddress?.neighbourhood?.name, requestedAddress?.provinceOrState?.name),
      };
    }
  }

  const theme = tenant.theme;
  // SEO experiment: on the /houses sub-variant on area pages, make the canonical url point back to the default area page
  const sanitizedResolvedUrl = stripAffiliateHandle(context.resolvedUrl).replace(`/${AREA_LISTINGS_ROUTE}`, ''); //TODO: ideally we use query.areaPath instead, but that will require more tests
  let canonicalUrl = generateCanonicalUrl({
    asPath: sanitizedResolvedUrl,
    baseHost: theme.schemaUrl,
    filters: fullFilter,
    locale: context.locale || tenant.localization.defaultLanguage,
    tenantId: tenant.id,
  });
  if (requestedAddress.seoFilters?.homeType === 'houses') {
    canonicalUrl = canonicalUrl.replace('/houses', '');
  }

  let headTags: HeadDataType | null = null;
  const hrefLangs = generateHreflangFromCanonicalUrl({
    canonicalUrl,
    locale: context.locale || tenant.localization.defaultLanguage,
    tenantId: tenant.id,
  });

  // Get current locale for schema inLanguage property
  const localeManager = new LocaleManager(tenant.id, context.locale, context.req.headers.cookie);
  const currentLocale = localeManager.getCurrentLocale();

  if (isUnknownAddress) {
    const provOrState = requestedAddress?.provinceOrState;
    const subDivision = requestedAddress?.subDivision;
    const neighbourhood = requestedAddress?.neighbourhood;
    const isInsideProvinceOrState = !!subDivision && !!provOrState;
    const areaName = neighbourhood && isInsideProvinceOrState ? `${neighbourhood?.name}, ${provOrState?.name}` : neighbourhood?.name
      || subDivision && isInsideProvinceOrState ? `${subDivision?.name}, ${provOrState?.name}` : subDivision?.name
      || requestedAddress.provinceOrState?.name
      || requestedAddress.country?.name
      || '';
    headTags = new HeadTagsBuilder()
      .setLocale(currentLocale)
      .setCanonicalUrl(canonicalUrl)
      .setTitle({ siteName: theme.name, areaName, filters: fullFilter, city: requestedAddress.subDivision?.name, neighbourhood: requestedAddress.neighbourhood?.name }, i18n?.t)
      .setMetaDescription(areaName, fullFilter, TotalCount, i18n?.t)
      .setHrefLangs(hrefLangs)
      .build();
  } else if (isFallbackArea) {
    const address = requestedAreaPageAddress[requestedAreaPageAddress.length - 1];
    const isInsideProvinceOrState = !!address.subDivision && !!address.provinceOrState;
    const areaName = isInsideProvinceOrState ? `${address.label}, ${address.provinceOrState}` : address.label;
    headTags = new HeadTagsBuilder()
      .setLocale(currentLocale)
      .setCanonicalUrl(canonicalUrl)
      .setTitle({ siteName: theme.name, areaName, filters: fullFilter, city: address.subDivision, neighbourhood: address.neighbourhood }, i18n?.t)
      .setMetaDescription(areaName, fullFilter, TotalCount, i18n?.t)
      .setBreadcrumbList(breadcrumbs)
      .setHrefLangs(hrefLangs)
      .build();
  } else {
    const isInsideProvinceOrState = !!electedAddress.subDivision && !!electedAddress.provinceOrState;
    const areaName = isInsideProvinceOrState ? `${electedAddress.label}, ${electedAddress.provinceOrState}` : electedAddress.label;
    const country = electedAddress.countryCode as CountryCode;
    const provinceOrState = electedAddress.provinceOrState?.toLowerCase() as ProvinceOrStateCode;
    const city = electedAddress.subDivision;
    const neighbourhood = electedAddress.neighbourhood;

    let propertyInsights: PropertyInsights | null = null;
    if (country && provinceOrState) { // County stats have not been implemented yet
      const cityStats = await insightsApi.getCityStats(country, provinceOrState, city);
      if (cityStats) {
        propertyInsights = {
          averagePrices: {
            condo: cityStats.AvgCondoPrice,
            house: cityStats.AvgHousePrice,
            townhouse: cityStats.AvgTownhousePrice,
            total: cityStats.AvgTotalPrice,
          },
          counts: {
            condo: cityStats.TotalCondos,
            house: cityStats.TotalHouses,
            townhouse: cityStats.TotalTownhouses,
          },
          totalListings: TotalCount,
        };
      }
    }

    headTags = buildHeadTags({
      canonicalUrl,
      location: {
        areaName,
        country,
        administrativeArea: provinceOrState,
        city,
        neighbourhood,
      },
      filters: fullFilter,
      propertyInsights,
      siteName: theme.name,
      lowestPrice,
      highestPrice,
      hrefLangs,
      locale: currentLocale,
      tenantId: tenant.id,
      t: i18n?.t,
    });
  }

  const seoConfig = getTenantSeoConfig(tenant);
  headTags = applySeoRules(headTags, AREA_LISTINGS_ROUTE, seoConfig);


  // For crawlers and lighthouse, we need to generate a map snapshot url to show a static map instead of loading the interactive map
  let mapkitSnapshotUrl = '';
  if (isCrawlerOrLighthouse) {
    const formattedElectedAddress = removeUndefined(electedAddress);
    const formattedAddressSearchQuery = [
      formattedElectedAddress.neighbourhood,
      formattedElectedAddress.subDivision,
      formattedElectedAddress.provinceOrState,
    ].filter(Boolean).join(', ');
    const token = getMapKitToken(tenant.id);
    mapkitSnapshotUrl = `https://snapshot.apple-mapkit.com/api/v1/snapshot?center=${formattedAddressSearchQuery}&token=${token}&size=640x480`;
  }

  const areaInsights = await getAreaPageV2Insights(resolvedPageAddress ?? electedAddress, areaNameText, resolvedPageAddress?.slug, insightsApi, getInsightsListingType(fullFilter), context.locale);

  return {
    props: {
      routeName: AREA_LISTINGS_ROUTE,
      props: {
        features,
        tenantId: tenant.id,
        headTags: removeUndefined(headTags),
        requestedAddress: removeUndefined(requestedAddress),
        electedAddress: removeUndefined(electedAddress),
        breadcrumbs: breadcrumbs || [],
        listings: listings.map<AreaPageListing>((listing: AreaPageListing) => AreaPageListing.toJSON(listing) as AreaPageListing),
        heading,
        headingWithoutCount,
        citySlug: resolvedPageAddress?.slug,
        pagination: {
          page: PageNumber,
          size: PageSize,
          count: TotalCount,
          total: Math.min(AREA_PAGE_CONSTANTS.MAX_PAGE_COUNT, TotalPages),
        },
        nextPageCursor: getListingsResponse?.nextPageCursor || null,
        areaGuideData: areaGuideData ? removeUndefined(areaGuideData) : null,
        internalLinks,
        seoLinks: seoLinks || null,
        expandGuidesByDefault,
        myLinkMyLead: tenant.features.MyLinkMyLead || false,
        useListingCardWithCompliance: false,
        mustHideExpiredListings: false,
        showHybridAreaMapPage: tenant.features.ShowHybridAreaMapPage || false,
        isCrawler: isCrawlerOrLighthouse,
        mapkitSnapshotUrl,
        missingAddress,
        areaInsights,
      },
    },
  };
}

//#region Utility Functions

async function getAreaPageV2Insights(
  electedAddress: SingleAddress,
  locationName: string,
  citySlug: string | undefined,
  insightsApi: InsightsApiType,
  listingType: InsightsListingType,
  locale?: string
): Promise<AreaPageV2AreaInsights | null> {
  const country = electedAddress.countryCode as CountryCode;
  const provinceOrState = electedAddress.provinceOrState?.toLowerCase() as ProvinceOrStateCode | undefined;
  if (!country || !provinceOrState) {
    return null;
  }

  const slug = citySlug || electedAddress.slug || '';
  const city = electedAddress.subDivision || '';

  let cityStats: AreaPageV2AreaInsights['cityStats'] = null;
  let storedCityInsights: StoredCityInsights | null = null;
  try {
    [cityStats, storedCityInsights] = await Promise.all([
      insightsApi.getCityStats(country, provinceOrState, city || undefined, undefined, listingType).then(stats => stats ?? null),
      slug ? getCityInsights(slug).catch(() => null) : Promise.resolve(null),
    ]);
  } catch (error) {
    console.error(`Failed to resolve AreaPageV2 insights server-side: ${error instanceof Error ? error.message : error}`);
  }

  const cityInsights = storedCityInsights ? selectLocalizedCityInsights(storedCityInsights, locale) : null;
  const requestedLang = normalizeLocaleToInsightsLang(locale);
  const insightsGenerateLocale = !storedCityInsights
    ? requestedLang
    : isLocaleTranslationMissing(storedCityInsights, locale)
      ? requestedLang
      : null;

  return removeUndefined({
    cityStats,
    cityStatsListingType: listingType,
    cityInsights,
    insightsGenerateLocale,
    locationName: locationName || electedAddress.label || '',
    locationUrl: slug ? `/${slug}-real-estate` : '',
    citySlug: slug,
    city,
    province: provinceOrState,
    country,
  });
}

async function getInternalLinks(address: AddressHierarchyPath, lat: number, long: number, tenant: TenantTypes, isSoldFilterOn: boolean) {
  if (address.type === AddressType.ADDRESS_TYPE_SUB_DIVISION) {
    const slug = address.slug;
    const city = capitalizeWords(address.subDivision.label);
    const province = address.provinceOrState.provinceOrState.toUpperCase() as ProvinceAndState;
    return await generateLocalizedCityFooterData(city, slug, tenant, province, 10, undefined, isSoldFilterOn);
  } else if (address.type === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD) {
    const city = capitalizeWords(address.subDivision.label);
    const city_slug = address.subDivision.slug;
    const neighbourhood_slug = address.slug;
    const neighbourhood = capitalizeWords(deDasherize(address.neighbourhood.label));
    const province = address.provinceOrState.provinceOrState.toUpperCase() as ProvinceAndState;
    return await generateLocalizedNeighbourhoodFooterData(city, city_slug, neighbourhood, neighbourhood_slug, province, lat, long, tenant);
  }
  return []; // Return empty array as default case
}

async function getSeoLinks(electedAddress: SingleAddress, seoLinksApi: SeoLinksApiType, isSoldFilterOn: boolean) {
  switch (electedAddress.addressType) {
    case AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE:
      return await seoLinksApi.getLocalizedCityLinks({
        country: electedAddress.countryCode as CountryCode,
        province: electedAddress.provinceOrState as ProvinceOrStateCode,
        limit: 9000,
        sorted: true,
        isSold: isSoldFilterOn,
      });
    case AddressType.ADDRESS_TYPE_COUNTRY:
      return await seoLinksApi.getLocalizedProvinceLinks({
        country: electedAddress.countryCode as CountryCode,
        sorted: true,
        isSold: isSoldFilterOn,
      });
    default:
      return null;
  }
}

function generateAreaBlurbHeading(city = '', neighbourhood = '', provinceOrState = '') {
  if (neighbourhood?.length > 0) {
    return `About ${neighbourhood}, ${provinceOrState}`;
  }
  if (city?.length > 0) {
    return `About ${city}, ${provinceOrState}`;
  }
  if (provinceOrState?.length > 0) {
    return `About ${provinceOrState}`;
  }
  return '';
}
//#endregion
